import { useEffect, useState } from 'react';
import { BIBLE_BOOKS } from '../../../shared/types';
import { apiClient } from '../hooks/useApi';
import BottomNav from '../components/BottomNav';
import { barColor, calcProgressPct } from '../utils/progressUtils';

interface BookProgressData {
  name: string;
  totalChapters: number;
  completedChapters: number;
}

const ProgressPage = () => {
  const [bookProgress, setBookProgress] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentReadThrough, setCurrentReadThrough] = useState<number>(1);
  const [selectedReadThrough, setSelectedReadThrough] = useState<number>(1);

  // Load profile to get currentReadThrough, then load progress
  useEffect(() => {
    apiClient.getProfile().then((profile) => {
      const rt = (profile as any)?.currentReadThrough ?? 1;
      setCurrentReadThrough(rt);
      setSelectedReadThrough(rt);
      return apiClient.getProgress(undefined, rt);
    }).then((data: unknown) => {
      const d = data as { bookProgress?: Record<string, number> };
      setBookProgress(d.bookProgress ?? {});
    }).catch(() => setError('Failed to load progress. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  // Reload progress when the user switches read-through tab
  const handleSelectReadThrough = (rt: number) => {
    if (rt === selectedReadThrough) return;
    setSelectedReadThrough(rt);
    setLoading(true);
    apiClient.getProgress(undefined, rt)
      .then((data: unknown) => {
        const d = data as { bookProgress?: Record<string, number> };
        setBookProgress(d.bookProgress ?? {});
      })
      .catch(() => setError('Failed to load progress. Please try again.'))
      .finally(() => setLoading(false));
  };

  const books: BookProgressData[] = BIBLE_BOOKS.map((b) => ({
    name: b.name,
    totalChapters: b.chapters,
    completedChapters: bookProgress[b.name] ?? 0,
  }));

  const totalCompleted = books.reduce((s, b) => s + b.completedChapters, 0);
  const totalChapters = books.reduce((s, b) => s + b.totalChapters, 0);
  const overallPct = calcProgressPct(totalCompleted, totalChapters);

  return (
    <div className="min-h-screen bg-parchment-50 pb-20">
      {/* Header */}
      <div className="bg-parchment-100 border-b border-parchment-200 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <span className="text-2xl">📖</span>
          <h1 className="text-xl font-bold text-ink">TrackBible</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-ink">Bible Progress</h2>
          <p className="text-ink-muted text-sm mt-1">
            {totalCompleted} / {totalChapters} chapters read
          </p>
        </div>

        {/* Read-through tabs — only shown when the user has started more than 1 read-through */}
        {currentReadThrough > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {Array.from({ length: currentReadThrough }, (_, i) => i + 1).map((rt) => (
              <button
                key={rt}
                onClick={() => handleSelectReadThrough(rt)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition
                  ${selectedReadThrough === rt
                    ? 'bg-leather text-parchment-100 shadow-sm'
                    : 'bg-parchment-100 border border-parchment-300 text-ink-muted hover:bg-parchment-200'
                  }`}
              >
                {rt === 1 ? 'First Read' : rt === 2 ? 'Second Read' : rt === 3 ? 'Third Read' : `Read ${rt}`}
              </button>
            ))}
          </div>
        )}

        {/* Overall progress bar */}
        <div className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-ink-muted">Overall</span>
            <span className="text-sm font-bold text-leather">{overallPct}%</span>
          </div>
          <div className="h-3 bg-parchment-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${overallPct === 0 ? 'bg-parchment-300' : overallPct === 100 ? 'bg-leather-dark' : 'bg-leather'}`}
              style={{ width: `${overallPct}%` }}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-4 border-leather border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">{error}</div>
        ) : (
          <>
            {/* Old Testament */}
            <section>
              <h3 className="text-sm font-semibold text-ink-muted uppercase tracking-wider mb-3">
                Old Testament
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {books
                  .filter((_, i) => BIBLE_BOOKS[i].testament === 'old')
                  .map((book) => {
                    const pct = calcProgressPct(book.completedChapters, book.totalChapters);
                    return (
                      <div
                        key={book.name}
                        className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-3"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium text-ink">{book.name}</span>
                          <span className="text-xs text-ink-muted">
                            {book.completedChapters}/{book.totalChapters}
                          </span>
                        </div>
                        <div className="h-2 bg-parchment-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${barColor(pct)}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </section>

            {/* New Testament */}
            <section>
              <h3 className="text-sm font-semibold text-ink-muted uppercase tracking-wider mb-3">
                New Testament
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {books
                  .filter((_, i) => BIBLE_BOOKS[i].testament === 'new')
                  .map((book) => {
                    const pct = calcProgressPct(book.completedChapters, book.totalChapters);
                    return (
                      <div
                        key={book.name}
                        className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-3"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium text-ink">{book.name}</span>
                          <span className="text-xs text-ink-muted">
                            {book.completedChapters}/{book.totalChapters}
                          </span>
                        </div>
                        <div className="h-2 bg-parchment-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${barColor(pct)}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </section>
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default ProgressPage;
